<script>
    import {
        NotificationModal,
        TableComponentContainer,
    } from "../../../../Common/index.svelte";

    import {
        getEncounterStatus,
        isObjectEmpty,
        showError,
    } from "../../../../../Utils/utilFunctions.svelte";

    import { encounterStatus } from "../../../../../Utils/utilEnums.svelte";

    import { apiMethods } from "../../../../../Services/index.svelte";
    import { checkPatientPaidPaymentsViaChangeEncounterStatus } from "../../../../../Utils/utilConstants.svelte";

    export let row = {};
    export let onStatusChange = () => {};

    let showStatusDropdown = false;
    let dropdownPosition = { top: 0, left: 0 };

    let selectedStatus,
        showUpdateStatusModal = false,
        showCancelEncounterWhenPatientPaysModal = false,
        isUpdatingEncounterStatus = false;

    const updateEncounterStatus = async (addSkipRefundToUrl = false) => {
        isUpdatingEncounterStatus = true;

        if (addSkipRefundToUrl)
            await apiMethods.putMethod
                .cancelEncounterWithoutRefund(row.id)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to cancel encounter, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else onStatusChange();
                });
        else
            await apiMethods.putMethod
                .updateEncounterStatus(row.id, selectedStatus)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to update encounter status, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else onStatusChange();
                });

        isUpdatingEncounterStatus = false;

        _closeUpdateStatusModal();
    };

    const toggleStatusDropdown = (event) => {
        showStatusDropdown = !showStatusDropdown;

        const { top, left, height, width } =
            event.target.getBoundingClientRect();
        const dropdownWidth = 220;
        const dropdownHeight = 300;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const tolerance = 5;

        let dropdownLeft = left + window.scrollX;
        let dropdownTop = top + height + window.scrollY;

        if (dropdownLeft + dropdownWidth > viewportWidth - tolerance)
            dropdownLeft = left + window.scrollX - dropdownWidth + width;

        if (dropdownTop + dropdownHeight > viewportHeight - tolerance)
            dropdownTop = top + window.scrollY - dropdownHeight;

        if (dropdownLeft < tolerance) dropdownLeft = tolerance;
        if (dropdownTop < tolerance) dropdownTop = tolerance;

        dropdownPosition = {
            top: dropdownTop,
            left: dropdownLeft,
        };
    };

    const closeDropdown = () => (showStatusDropdown = false);

    // HELPER FUNCTIONS
    const _openUpdateStatusModal = async (status) => {
        if (status == row.status) return;

        selectedStatus = status;

        if (
            checkPatientPaidPaymentsViaChangeEncounterStatus.includes(
                selectedStatus,
            )
        )
            await _handleCancelEncounterWithPatientPayments();
        else showUpdateStatusModal = true;

        closeDropdown();
    };

    const _closeUpdateStatusModal = () => {
        selectedStatus = null;
        showUpdateStatusModal = false;
        showCancelEncounterWhenPatientPaysModal = false;
    };

    const _handleCancelEncounterWithPatientPayments = async () => {
        let encounterFullDetails = {};

        await apiMethods.getMethod
            .getPatientEncounterById(row.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to get encounter details, please try again later or contact support...",
                    );
                else encounterFullDetails = { ...res };
            });

        if (isObjectEmpty(encounterFullDetails)) showUpdateStatusModal = true;
        else {
            const { estimates, invoices } = encounterFullDetails;

            let stopCheck = false;

            if (!!estimates.length)
                estimates.map((est) => {
                    if (
                        est.payments.length &&
                        est.payments.some(
                            ({ paidBy, status }) =>
                                paidBy == 0 && [1, 2].includes(status),
                        )
                    ) {
                        showCancelEncounterWhenPatientPaysModal = true;
                        stopCheck = true;
                        return;
                    }
                });

            if (!stopCheck && !!invoices.length)
                invoices.map((inv) => {
                    if (
                        inv.payments.length &&
                        inv.payments.some(
                            ({ paidBy, status }) =>
                                paidBy == 0 && [1, 2].includes(status),
                        )
                    ) {
                        showCancelEncounterWhenPatientPaysModal = true;
                        stopCheck = true;
                        return;
                    }
                });

            if (!stopCheck) showUpdateStatusModal = true;
        }
    };
</script>

<TableComponentContainer customContainerClass="min-w-[120px] whitespace-nowrap">
    <div
        class="flex flex-1 w-fit items-center justify-start"
        on:click={toggleStatusDropdown}
    >
        <div
            class="flex items-center gap-x-2 p-1 px-2 rounded rounded-full"
            style="background-color: {getEncounterStatus(
                row.status,
                'bgColor',
            )}; cursor: pointer;"
        >
            <div
                class="w-2 h-2 rounded rounded-full"
                style="background-color: {getEncounterStatus(
                    row.status,
                    'color',
                )};"
            />

            <div
                style="color: {getEncounterStatus(
                    row.status,
                    'color',
                )}; padding: 2px 2px 0 0;"
            >
                {getEncounterStatus(row.status)}
            </div>
        </div>
    </div>

    {#if showStatusDropdown}
        <div
            class="dropdown"
            style="top: {dropdownPosition.top}px; left: {dropdownPosition.left}px;"
        >
            <div class="flex flex-1 w-full flex-col gap-y-1">
                {#each encounterStatus as status}
                    <div
                        class="optionsContainer flex w-full cursor-pointer p-2 rounded rounded-lg {status.id ==
                        row.status
                            ? 'bg-[#634DE3]'
                            : ''}"
                        on:click={() => _openUpdateStatusModal(status.id)}
                    >
                        <div
                            class="flex items-center gap-x-2 p-1 px-2 rounded rounded-full"
                            style="background-color: {status.bgColor};"
                        >
                            <div
                                class="w-2 h-2 rounded rounded-full"
                                style="background-color: {status.color};"
                            />
                            <div
                                style="color: {status.color}; padding: 2px 2px 0 0;"
                            >
                                {status.type}
                            </div>
                        </div>
                    </div>
                {/each}
            </div>
        </div>

        <div
            class="fixed top-0 left-0 w-full h-full"
            on:click={closeDropdown}
        />
    {/if}
</TableComponentContainer>

<NotificationModal
    isModalOpen={showUpdateStatusModal}
    title="Update Encounter Status"
    cancelText="Not Now"
    closeModal={_closeUpdateStatusModal}
    closeModalFunction={_closeUpdateStatusModal}
    submitText={isUpdatingEncounterStatus ? "Updating" : "Update"}
    confirmAction={() => updateEncounterStatus()}
    isConfirming={isUpdatingEncounterStatus}
    cancelButtonClass="btn-accent"
>
    <p class="text-left leading-6 p-4">
        You are about to update the status of this encounter to {getEncounterStatus(
            selectedStatus,
        )}.
    </p>
</NotificationModal>

<NotificationModal
    isModalOpen={showCancelEncounterWhenPatientPaysModal}
    title="Update Encounter Status"
    modalMaxWidth="max-w-xl"
    showCancelBtn={false}
    showConfirmBtn={false}
    closeModalFunction={_closeUpdateStatusModal}
    isConfirming={isUpdatingEncounterStatus}
    btnOptions={[
        {
            text: "Cancel",
            class: "btn-accent w-auto",
            loading: isUpdatingEncounterStatus,
            function: () => _closeUpdateStatusModal(),
        },
        {
            text: isUpdatingEncounterStatus
                ? "Confirming"
                : "Confirm, No Refund",
            class: "btn-secondary text-xs w-auto",
            loading: isUpdatingEncounterStatus,
            function: () => updateEncounterStatus(true),
        },
        {
            text: isUpdatingEncounterStatus
                ? "Confirming"
                : "Confirm and Refund",
            class: "btn-secondary text-xs w-auto",
            loading: isUpdatingEncounterStatus,
            function: () => updateEncounterStatus(),
        },
    ]}
>
    <p class="text-left leading-6 p-4">
        Change encounter status to Cancelled and refund patient?
    </p>
</NotificationModal>

<style>
    .dropdown {
        position: fixed;
        z-index: 1000;
        background: white;
        border: 1px solid #ccc;
        box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);
        border-radius: 8px;
        padding: 8px;
        min-width: 220px;
        max-height: 400px;
        overflow-y: auto;
    }

    .optionsContainer:hover {
        background-color: #634de3;
    }
</style>
